import { lazy } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';

import { AdminLayout } from './components/admin/AdminLayout';
import { Layout } from './components/Layout';
import { ManageLayout } from './components/manage/ManageLayout';

// Customer pages
const AccountPage = lazy(() =>
  import('./pages/Account.js').then((m) => ({ default: m.AccountPage })),
);
const AuthCallbackPage = lazy(() =>
  import('./pages/AuthCallback.js').then((m) => ({ default: m.AuthCallbackPage })),
);
const CartPage = lazy(() => import('./pages/Cart.js').then((m) => ({ default: m.CartPage })));
const CheckoutPage = lazy(() =>
  import('./pages/Checkout.js').then((m) => ({ default: m.CheckoutPage })),
);
const CookiesPage = lazy(() =>
  import('./pages/Cookies.js').then((m) => ({ default: m.CookiesPage })),
);
const HomePage = lazy(() => import('./pages/Home.js').then((m) => ({ default: m.HomePage })));
const LoginPage = lazy(() => import('./pages/Login.js').then((m) => ({ default: m.LoginPage })));
const NotFoundPage = lazy(() =>
  import('./pages/NotFound.js').then((m) => ({ default: m.NotFoundPage })),
);
const OrderReviewPage = lazy(() =>
  import('./pages/OrderReview.js').then((m) => ({ default: m.OrderReviewPage })),
);
const OrderStatusPage = lazy(() =>
  import('./pages/OrderStatus.js').then((m) => ({ default: m.OrderStatusPage })),
);
const PrivacyPage = lazy(() =>
  import('./pages/Privacy.js').then((m) => ({ default: m.PrivacyPage })),
);
const RestaurantPage = lazy(() =>
  import('./pages/Restaurant.js').then((m) => ({ default: m.RestaurantPage })),
);
const TermsPage = lazy(() => import('./pages/Terms.js').then((m) => ({ default: m.TermsPage })));

// Backoffice pages
const ManageHomePage = lazy(() =>
  import('./pages/manage/ManageHome.js').then((m) => ({ default: m.ManageHomePage })),
);
const OnboardingPage = lazy(() =>
  import('./pages/manage/Onboarding.js').then((m) => ({ default: m.OnboardingPage })),
);
const DashboardPage = lazy(() =>
  import('./pages/manage/Dashboard.js').then((m) => ({ default: m.DashboardPage })),
);
const MenuPage = lazy(() =>
  import('./pages/manage/Menu.js').then((m) => ({ default: m.MenuPage })),
);
const OrdersPage = lazy(() =>
  import('./pages/manage/Orders.js').then((m) => ({ default: m.OrdersPage })),
);
const ReviewsPage = lazy(() =>
  import('./pages/manage/Reviews.js').then((m) => ({ default: m.ReviewsPage })),
);
const HoursPage = lazy(() =>
  import('./pages/manage/Hours.js').then((m) => ({ default: m.HoursPage })),
);
const DeliveryPage = lazy(() =>
  import('./pages/manage/Delivery.js').then((m) => ({ default: m.DeliveryPage })),
);
const TeamPage = lazy(() =>
  import('./pages/manage/Team.js').then((m) => ({ default: m.TeamPage })),
);
const PayoutsPage = lazy(() =>
  import('./pages/manage/Payouts.js').then((m) => ({ default: m.PayoutsPage })),
);
const VouchersPage = lazy(() =>
  import('./pages/manage/Vouchers.js').then((m) => ({ default: m.VouchersPage })),
);
const SettingsPage = lazy(() =>
  import('./pages/manage/Settings.js').then((m) => ({ default: m.SettingsPage })),
);

// Admin pages
const AdminStatsPage = lazy(() =>
  import('./pages/admin/StatsPage.js').then((m) => ({ default: m.StatsPage })),
);
const AdminRestaurantsPage = lazy(() =>
  import('./pages/admin/RestaurantsPage.js').then((m) => ({ default: m.RestaurantsPage })),
);
const AdminRestaurantDetailPage = lazy(() =>
  import('./pages/admin/RestaurantDetailPage.js').then((m) => ({
    default: m.RestaurantDetailPage,
  })),
);
const AdminDisputesPage = lazy(() =>
  import('./pages/admin/DisputesPage.js').then((m) => ({ default: m.DisputesPage })),
);
const AdminReviewsPage = lazy(() =>
  import('./pages/admin/ReviewsPage.js').then((m) => ({ default: m.AdminReviewsPage })),
);
const AdminVouchersPage = lazy(() =>
  import('./pages/admin/VouchersPage.js').then((m) => ({ default: m.AdminVouchersPage })),
);

const routes: RouteObject[] = [
  {
    path: 'admin',
    element: <AdminLayout />,
    children: [
      { index: true, element: <AdminStatsPage /> },
      { path: 'restaurants', element: <AdminRestaurantsPage /> },
      { path: 'restaurants/:restaurantId', element: <AdminRestaurantDetailPage /> },
      { path: 'disputes', element: <AdminDisputesPage /> },
      { path: 'reviews', element: <AdminReviewsPage /> },
      { path: 'vouchers', element: <AdminVouchersPage /> },
    ],
  },
  {
    path: 'manage',
    element: <ManageLayout />,
    children: [
      { index: true, element: <ManageHomePage /> },
      { path: 'onboarding', element: <OnboardingPage /> },
      {
        path: ':restaurantId',
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'menu', element: <MenuPage /> },
          { path: 'orders', element: <OrdersPage /> },
          { path: 'reviews', element: <ReviewsPage /> },
          { path: 'hours', element: <HoursPage /> },
          { path: 'delivery', element: <DeliveryPage /> },
          { path: 'team', element: <TeamPage /> },
          { path: 'payouts', element: <PayoutsPage /> },
          { path: 'vouchers', element: <VouchersPage /> },
          { path: 'settings', element: <SettingsPage /> },
        ],
      },
    ],
  },
  {
    element: <Layout />,
    children: [
      { index: true, element: <HomePage /> },
      { path: 'restaurants/:slug', element: <RestaurantPage /> },
      { path: 'cart', element: <CartPage /> },
      { path: 'checkout', element: <CheckoutPage /> },
      { path: 'orders', element: <Navigate replace to="/account" /> },
      { path: 'orders/:orderId', element: <OrderStatusPage /> },
      { path: 'orders/:orderId/review', element: <OrderReviewPage /> },
      { path: 'account', element: <AccountPage /> },
      { path: 'account/login', element: <LoginPage /> },
      { path: 'account/callback', element: <AuthCallbackPage /> },
      { path: 'legal/terms', element: <TermsPage /> },
      { path: 'legal/privacy', element: <PrivacyPage /> },
      { path: 'legal/cookies', element: <CookiesPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];

export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter(routes);
